import { RouterModule, Routes } from '@angular/router';
import { NgModule } from '@angular/core';
import { AppMainComponent } from './app.main.component';
import { AppNotfoundComponent } from './pages/app.notfound.component';
import { AppErrorComponent } from './pages/app.error.component';
import { AppAccessdeniedComponent } from './pages/app.accessdenied.component';
import { AngularFireAuthGuard, customClaims, redirectUnauthorizedTo, redirectLoggedInTo } from '@angular/fire/compat/auth-guard';
import { map, pipe } from 'rxjs';

const redirectLoggedInToDashboard = () => redirectLoggedInTo(['dashboard']);

/*
  Al panel solo entra quien administra.

  Todas las rutas usaban `onlyAdmins`, que solo pregunta si hay sesión — y la
  sesión es la misma de la web pública, donde cualquiera se registra. Así que quien se apuntaba a
  una carrera podía abrir la dirección del panel y pasearse por dentro: la API le contestaba 403 a
  todo, pero el panel entraba igual y el dashboard lee las estadísticas directamente de Firestore,
  que sí le dejaba (ver `firestore.rules`).

  El claim que se pone al crear un usuario es `role: "admin"` —lo escribe `UsersFbService`— y no un
  claim llamado `admin`, que es lo que miraba el `hasCustomClaim('admin')` que había escrito aquí y
  no se llegó a usar en ninguna ruta.
*/
const onlyAdmins = () => pipe(
  customClaims,
  map((claims) => {
    if (!claims || Object.keys(claims).length === 0) return ['sign-in'];

    return claims['role'] === 'admin' ? true : ['access'];
  }),
);

/*export const routes: Routes = [
  { path: '', component: AppComponent },
  { path: 'login', component: LoginComponent, canActivate: [AngularFireAuthGuard], data: { authGuardPipe: redirectLoggedInToDashboard } },
  { path: 'items', component: ItemListComponent, canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins } },
  { path: 'admin', component: AdminComponent, canActivate: [AngularFireAuthGuard], data: { authGuardPipe: adminOnly } },
  { path: 'accounts/:id', component: AdminComponent, canActivate: [AngularFireAuthGuard], data: { authGuardPipe: belongsToAccount } }
];*/

const routes: Routes = [
  {
    path: '',
    loadChildren: () =>
      import('src/app/auth/auth.module').then((m) => m.AuthModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: redirectLoggedInToDashboard },
  },
  {
    path: 'dashboard',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/dashboard/dashboard.module')
        .then(m => m.DashboardModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'categories',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/categories/categories.module')
        .then(m => m.CategoriesModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  }, {
    path: 'sponsors',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/sponsors/sponsors.module')
        .then(m => m.SponsorsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  }, {
    path: 'circuits',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/circuits/circuits.module')
        .then(m => m.CircuitsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  }, {
    path: 'events',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/competitions/competitions.module')
        .then(m => m.CompetitionsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  }, {
    path: 'enrollments',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/enrollments/enrollments.module')
        .then(m => m.EnrollmentsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'runners',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/runners/runners.module')
        .then(m => m.RunnersModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'configurations',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/configurations/configurations.module')
        .then(m => m.ConfigurationsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'news',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/news/news.module')
        .then(m => m.NewsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'mailings',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/mailings/mailings.module')
        .then(m => m.MailingsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'bib0s',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/bib0s/bib0s.module')
        .then(m => m.Bib0sModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'documents',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/documents/documents.module')
        .then(m => m.DocumentsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'users',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/users/users.module')
        .then(m => m.UsersModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'clubs',
    component: AppMainComponent,
    loadChildren: () =>
      import('src/app/pages/clubs/clubs.module')
        .then(m => m.ClubsModule),
    canActivate: [AngularFireAuthGuard], data: { authGuardPipe: onlyAdmins },
  },
  {
    path: 'error',
    component: AppErrorComponent
  },
  { path: 'access', component: AppAccessdeniedComponent },
  { path: 'notfound', component: AppNotfoundComponent },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
]

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
